import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * CourseBestPracticesAuditApp
 * -----------------------------------------------------------
 * Full reviewable audit UI:
 * - Select scope (course IDs / hierarchy node / data source key)
 * - Run audit
 * - Review detected values
 * - Override or supply missing values
 * - Recalculate scores
 * - Mark courses reviewed
 * - Generate final scorecard
 * - Download PDF (generated in browser)
 *
 * Props:
 *   - platformUrl
 *   - backEndUrl
 *   - tenantId
 *
 * Query params:
 *   ?token=...&uuid=...
 */

/** ---------- Backend API adapter (tenant-aware) ---------- */
function makeCourseAuditApi(backEndUrl, token, tenantId) {
    const base = (backEndUrl || "").replace(/\/+$/, "");

    const http = axios.create({
        baseURL: base,
        timeout: 60000,
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(tenantId ? { "x-tenant-id": tenantId } : {}),
        },
    });

    return {
        startRun: async (payload) => {
            const { data } = await http.post("/api/course-audit/runs", payload);
            return data || {};
        },
        getRun: async (runId, params) => {
            const { data } = await http.get(`/api/course-audit/runs/${encodeURIComponent(runId)}`, {
                params,
            });
            return data || {};
        },
        recalculateRun: async (runId, payload) => {
            const { data } = await http.post(
                `/api/course-audit/runs/${encodeURIComponent(runId)}/recalculate`,
                payload
            );
            return data || {};
        },
        finalizeRun: async (runId, payload) => {
            const { data } = await http.post(
                `/api/course-audit/runs/${encodeURIComponent(runId)}/finalize`,
                payload
            );
            return data || {};
        },
    };
}

const CourseBestPracticesAuditApp = ({ platformUrl, backEndUrl, tenantId }) => {
    const location = useLocation();
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const token = queryParams.get("token") || "";
    const userUuid = queryParams.get("uuid") || "";
    const normalizedPlatformUrl = normalizeBase(platformUrl || "");
    const normalizedBackEndUrl = normalizeBase(backEndUrl || "");

    const auditApi = useMemo(
        () => makeCourseAuditApi(normalizedBackEndUrl, token, tenantId),
        [normalizedBackEndUrl, token, tenantId]
    );

    const [scopeType, setScopeType] = useState("courseIds");
    const [courseIdsText, setCourseIdsText] = useState("");
    const [hierarchyNodeId, setHierarchyNodeId] = useState("");
    const [dataSourceKey, setDataSourceKey] = useState("");

    const [includeFunctional, setIncludeFunctional] = useState(true);
    const [includeTechnical, setIncludeTechnical] = useState(true);
    const [includePedagogical, setIncludePedagogical] = useState(true);
    const [includeBehavior, setIncludeBehavior] = useState(true);

    const [loading, setLoading] = useState(false);
    const [polling, setPolling] = useState(false);
    const [saving, setSaving] = useState(false);
    const [finalizing, setFinalizing] = useState(false);

    const [runId, setRunId] = useState("");
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState("");

    const [result, setResult] = useState(null);
    const [selectedCourseId, setSelectedCourseId] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("functional");
    const [courseFilter, setCourseFilter] = useState("");
    const [showOnlyNeedsReview, setShowOnlyNeedsReview] = useState(false);
    const [showOnlyOverrides, setShowOnlyOverrides] = useState(false);
    const [showOnlyUnreviewed, setShowOnlyUnreviewed] = useState(false);

    const canRun = useMemo(() => {
        if (!token || !userUuid || !normalizedPlatformUrl || !normalizedBackEndUrl) return false;
        if (scopeType === "courseIds") return parseCourseIds(courseIdsText).length > 0;
        if (scopeType === "hierarchyNode") return !!hierarchyNodeId.trim();
        if (scopeType === "dataSourceKey") return !!dataSourceKey.trim();
        return false;
    }, [
        token,
        userUuid,
        normalizedPlatformUrl,
        normalizedBackEndUrl,
        scopeType,
        courseIdsText,
        hierarchyNodeId,
        dataSourceKey,
    ]);

    const summary = result?.summary || {
        totalCourses: 0,
        completedCourses: 0,
        averageOverallScore: null,
        averageFunctionalScore: null,
        averageTechnicalScore: null,
        averagePedagogicalScore: null,
        averageBehaviorScore: null,
        coursesReviewed: 0,
        metricsNeedingReview: 0,
        manualOverrides: 0,
    };

    const courses = result?.courses || [];

    useEffect(() => {
        if (!selectedCourseId && courses.length > 0) {
            setSelectedCourseId(courses[0].courseId);
        } else if (selectedCourseId && !courses.find((c) => c.courseId === selectedCourseId) && courses.length > 0) {
            setSelectedCourseId(courses[0].courseId);
        }
    }, [courses, selectedCourseId]);

    useEffect(() => {
        if (!runId || status !== "running") return;

        let cancelled = false;
        setPolling(true);

        const timer = setInterval(async () => {
            try {
                const data = await auditApi.getRun(runId, {
                    platformUrl: normalizedPlatformUrl,
                    userUuid,
                });

                if (cancelled) return;

                const hydrated = hydrateAuditResult(data);
                setResult(hydrated);

                const nextStatus = data?.status || "running";
                if (nextStatus === "complete" || nextStatus === "finalized") {
                    setStatus(nextStatus);
                    setPolling(false);
                    clearInterval(timer);
                } else if (nextStatus === "failed") {
                    setStatus("failed");
                    setError(data?.error || "Audit failed.");
                    setPolling(false);
                    clearInterval(timer);
                }
            } catch (e) {
                if (cancelled) return;
                setStatus("failed");
                setError(e?.response?.data?.message || e?.response?.data?.error || e?.message || "Failed to load audit status.");
                setPolling(false);
                clearInterval(timer);
            }
        }, 2500);

        return () => {
            cancelled = true;
            setPolling(false);
            clearInterval(timer);
        };
    }, [runId, status, auditApi, normalizedPlatformUrl, userUuid]);

    const filteredCourses = useMemo(() => {
        let list = [...courses];

        if (courseFilter.trim()) {
            const q = courseFilter.trim().toLowerCase();
            list = list.filter((c) =>
                String(c.courseName || "").toLowerCase().includes(q) ||
                String(c.courseId || "").toLowerCase().includes(q) ||
                String(c.dataSourceKey || "").toLowerCase().includes(q)
            );
        }

        if (showOnlyNeedsReview) {
            list = list.filter((c) => getCourseReviewStats(c).needsReviewCount > 0);
        }

        if (showOnlyOverrides) {
            list = list.filter((c) => getCourseReviewStats(c).overrideCount > 0);
        }

        if (showOnlyUnreviewed) {
            list = list.filter((c) => !c.reviewed);
        }

        return list;
    }, [courses, courseFilter, showOnlyNeedsReview, showOnlyOverrides, showOnlyUnreviewed]);

    const selectedCourse = useMemo(
        () =>
            filteredCourses.find((c) => c.courseId === selectedCourseId) ||
            courses.find((c) => c.courseId === selectedCourseId) ||
            null,
        [filteredCourses, courses, selectedCourseId]
    );

    const selectedMetrics = useMemo(() => {
        if (!selectedCourse) return [];
        const metrics = selectedCourse.metrics || [];
        return metrics.filter((m) => m.category === selectedCategory);
    }, [selectedCourse, selectedCategory]);

    const buildRunPayload = () => {
        const base = {
            platformUrl: normalizedPlatformUrl,
            userUuid,
            scopeType,
            checks: {
                functional: includeFunctional,
                technical: includeTechnical,
                pedagogical: includePedagogical,
                behavior: includeBehavior,
            },
        };

        if (scopeType === "courseIds") {
            return {
                ...base,
                courseIds: parseCourseIds(courseIdsText),
            };
        }

        if (scopeType === "hierarchyNode") {
            return {
                ...base,
                hierarchyNodeId: hierarchyNodeId.trim(),
            };
        }

        return {
            ...base,
            dataSourceKey: dataSourceKey.trim(),
        };
    };

    const startAudit = async () => {
        try {
            setError("");
            setLoading(true);
            setStatus("starting");
            setResult(null);
            setRunId("");
            setSelectedCourseId("");

            const data = await auditApi.startRun(buildRunPayload());

            const newRunId = data?.runId;
            if (!newRunId) throw new Error("No runId was returned.");

            setRunId(newRunId);
            setStatus("running");
        } catch (e) {
            setStatus("failed");
            setError(e?.response?.data?.message || e?.response?.data?.error || e?.message || "Failed to start audit.");
        } finally {
            setLoading(false);
        }
    };

    const refreshRun = async () => {
        if (!runId) return;
        try {
            setError("");
            const data = await auditApi.getRun(runId, {
                platformUrl: normalizedPlatformUrl,
                userUuid,
            });
            setResult(hydrateAuditResult(data));
            setStatus(data?.status || "complete");
        } catch (e) {
            setError(e?.response?.data?.message || e?.response?.data?.error || e?.message || "Failed to refresh audit.");
        }
    };

    const downloadPdf = () => {
        if (!result || !courses.length) return;

        const doc = new jsPDF({ unit: "pt", format: "letter" });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 40;
        const contentWidth = pageWidth - margin * 2;

        let y = margin;

        const title = "Course Best Practices Audit Report";
        const safeFileName = sanitizeFileName(`${title}-${runId || "run"}`);

        const summaryRows = [
            ["Run ID", runId || "—"],
            ["Status", status || "—"],
            ["Total Courses", `${summary.totalCourses || 0}`],
            ["Completed Courses", `${summary.completedCourses || 0}`],
            ["Courses Reviewed", `${summary.coursesReviewed || 0}`],
            ["Metrics Needing Review", `${summary.metricsNeedingReview || 0}`],
            ["Manual Overrides", `${summary.manualOverrides || 0}`],
            ["Avg Overall", fmtScore(summary.averageOverallScore)],
            ["Avg Functional", fmtScore(summary.averageFunctionalScore)],
            ["Avg Technical", fmtScore(summary.averageTechnicalScore)],
            ["Avg Pedagogical", fmtScore(summary.averagePedagogicalScore)],
            ["Avg Behavior", fmtScore(summary.averageBehaviorScore)],
        ];

        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.text(title, margin, y);
        y += 24;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);
        y = pdfText(
            doc,
            "Review detected values, reviewer overrides, highlights, recommendations, and calculated scores for all audited courses.",
            margin,
            y,
            contentWidth,
            14
        );
        y += 8;

        autoTable(doc, {
            startY: y,
            margin: { left: margin, right: margin },
            head: [["Metric", "Value"]],
            body: summaryRows,
            theme: "grid",
            styles: {
                fontSize: 10,
                cellPadding: 6,
                overflow: "linebreak",
                valign: "top",
            },
            headStyles: {
                fontStyle: "bold",
            },
            columnStyles: {
                0: { cellWidth: 180, fontStyle: "bold" },
                1: { cellWidth: "auto" },
            },
        });

        y = doc.lastAutoTable.finalY + 18;

        const courseSummaryRows = courses.map((course) => {
            const stats = getCourseReviewStats(course);
            return [
                course.courseName || "",
                course.courseId || "",
                fmtScore(course.overallScore),
                fmtScore(course.functionalScore),
                fmtScore(course.technicalScore),
                fmtScore(course.pedagogicalScore),
                fmtScore(course.behaviorScore),
                course.reviewed ? "Yes" : "No",
                `${stats.needsReviewCount}`,
                `${stats.overrideCount}`,
            ];
        });

        autoTable(doc, {
            startY: y,
            margin: { left: margin, right: margin },
            head: [[
                "Course",
                "Course ID",
                "Overall",
                "Functional",
                "Technical",
                "Pedagogical",
                "Behavior",
                "Reviewed",
                "Need Review",
                "Overrides",
            ]],
            body: courseSummaryRows.length
                ? courseSummaryRows
                : [["No courses", "", "", "", "", "", "", "", "", ""]],
            theme: "striped",
            styles: {
                fontSize: 9,
                cellPadding: 5,
                overflow: "linebreak",
                valign: "top",
            },
            headStyles: {
                fontStyle: "bold",
            },
            columnStyles: {
                0: { cellWidth: 110 },
                1: { cellWidth: 90 },
                2: { cellWidth: 45, halign: "right" },
                3: { cellWidth: 50, halign: "right" },
                4: { cellWidth: 50, halign: "right" },
                5: { cellWidth: 58, halign: "right" },
                6: { cellWidth: 50, halign: "right" },
                7: { cellWidth: 45, halign: "center" },
                8: { cellWidth: 52, halign: "right" },
                9: { cellWidth: 48, halign: "right" },
            },
        });

        y = doc.lastAutoTable.finalY + 16;

        courses.forEach((course, courseIndex) => {
            if (y > pageHeight - 180) {
                doc.addPage();
                y = margin;
            }

            doc.setFont("helvetica", "bold");
            doc.setFontSize(14);
            y = pdfText(
                doc,
                `${courseIndex + 1}. ${course.courseName || course.courseId || "Course"}`,
                margin,
                y,
                contentWidth,
                16
            );

            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);

            y = pdfText(doc, `Course ID: ${course.courseId || "—"}`, margin, y + 2, contentWidth, 13);

            if (course.dataSourceKey) {
                y = pdfText(doc, `Data Source Key: ${course.dataSourceKey}`, margin, y, contentWidth, 13);
            }

            if (course.templateCourseId) {
                y = pdfText(doc, `Template Course ID: ${course.templateCourseId}`, margin, y, contentWidth, 13);
            }

            y = pdfText(
                doc,
                `Scores — Overall: ${fmtScore(course.overallScore)} | Functional: ${fmtScore(course.functionalScore)} | Technical: ${fmtScore(course.technicalScore)} | Pedagogical: ${fmtScore(course.pedagogicalScore)} | Behavior: ${fmtScore(course.behaviorScore)}`,
                margin,
                y,
                contentWidth,
                13
            );

            y = pdfText(
                doc,
                `Reviewed: ${course.reviewed ? "Yes" : "No"}`,
                margin,
                y,
                contentWidth,
                13
            );

            y += 8;

            const metrics = course.metrics || [];
            const metricRows = metrics.map((metric) => [
                metric.label || metric.key || "",
                capitalize(metric.category || ""),
                metricDisplayValue(metric),
                statusLabel(metric.status),
                metric.required ? "Yes" : "No",
                fmtScore(metric.score),
                fmtScore(metric.maxScore),
                metric.notes || "",
            ]);

            autoTable(doc, {
                startY: y,
                margin: { left: margin, right: margin },
                head: [[
                    "Metric",
                    "Category",
                    "Final Value",
                    "Status",
                    "Required",
                    "Score",
                    "Max",
                    "Notes",
                ]],
                body: metricRows.length
                    ? metricRows
                    : [["No metrics", "", "", "", "", "", "", ""]],
                theme: "grid",
                styles: {
                    fontSize: 8,
                    cellPadding: 4,
                    overflow: "linebreak",
                    valign: "top",
                },
                headStyles: {
                    fontStyle: "bold",
                },
                columnStyles: {
                    0: { cellWidth: 120 },
                    1: { cellWidth: 60 },
                    2: { cellWidth: 90 },
                    3: { cellWidth: 55 },
                    4: { cellWidth: 45, halign: "center" },
                    5: { cellWidth: 40, halign: "right" },
                    6: { cellWidth: 40, halign: "right" },
                    7: { cellWidth: "auto" },
                },
            });

            y = doc.lastAutoTable.finalY + 12;

            if (Array.isArray(course.highlights) && course.highlights.length > 0) {
                if (y > pageHeight - 120) {
                    doc.addPage();
                    y = margin;
                }

                doc.setFont("helvetica", "bold");
                doc.setFontSize(11);
                doc.text("Highlights", margin, y);
                y += 10;

                doc.setFont("helvetica", "normal");
                doc.setFontSize(10);

                course.highlights.forEach((item) => {
                    y = pdfText(doc, `• ${item}`, margin + 10, y, contentWidth - 10, 13);
                    if (y > pageHeight - 60) {
                        doc.addPage();
                        y = margin;
                    }
                });

                y += 8;
            }

            if (Array.isArray(course.recommendations) && course.recommendations.length > 0) {
                if (y > pageHeight - 120) {
                    doc.addPage();
                    y = margin;
                }

                doc.setFont("helvetica", "bold");
                doc.setFontSize(11);
                doc.text("Recommendations", margin, y);
                y += 10;

                doc.setFont("helvetica", "normal");
                doc.setFontSize(10);

                course.recommendations.forEach((item) => {
                    y = pdfText(doc, `• ${item}`, margin + 10, y, contentWidth - 10, 13);
                    if (y > pageHeight - 60) {
                        doc.addPage();
                        y = margin;
                    }
                });

                y += 8;
            }

            if (courseIndex < courses.length - 1) {
                if (y > pageHeight - 60) {
                    doc.addPage();
                    y = margin;
                } else {
                    y += 8;
                }
            }
        });

        const pageCount = doc.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.text(`Page ${i} of ${pageCount}`, pageWidth - 90, pageHeight - 16);
        }

        doc.save(`${safeFileName}.pdf`);
    };

    const updateMetric = (courseId, metricKey, patch) => {
        setResult((prev) => {
            if (!prev) return prev;

            const nextCourses = (prev.courses || []).map((course) => {
                if (course.courseId !== courseId) return course;

                const nextMetrics = (course.metrics || []).map((metric) => {
                    if (metric.key !== metricKey) return metric;

                    const nextMetric = {
                        ...metric,
                        ...patch,
                    };

                    return deriveMetric(nextMetric);
                });

                return recalcCourseScores({ ...course, metrics: nextMetrics });
            });

            return recalcAuditSummary({
                ...prev,
                courses: nextCourses,
            });
        });
    };

    const markCourseReviewed = (courseId, reviewed) => {
        setResult((prev) => {
            if (!prev) return prev;

            const nextCourses = (prev.courses || []).map((course) => {
                if (course.courseId !== courseId) return course;
                return {
                    ...course,
                    reviewed,
                };
            });

            return recalcAuditSummary({
                ...prev,
                courses: nextCourses,
            });
        });
    };

    const saveRecalculated = async () => {
        if (!runId || !result) return;

        try {
            setSaving(true);
            setError("");

            const payload = buildReviewedPayload(result, normalizedPlatformUrl, userUuid);
            const data = await auditApi.recalculateRun(runId, payload);

            setResult(hydrateAuditResult(data));
            setStatus(data?.status || "complete");
        } catch (e) {
            setError(e?.response?.data?.message || e?.response?.data?.error || e?.message || "Failed to recalculate audit.");
        } finally {
            setSaving(false);
        }
    };

    const finalizeScorecard = async () => {
        if (!runId || !result) return;

        try {
            setFinalizing(true);
            setError("");
            setStatus("finalizing");

            const payload = buildReviewedPayload(result, normalizedPlatformUrl, userUuid);
            const data = await auditApi.finalizeRun(runId, payload);

            setResult(hydrateAuditResult(data));
            setStatus(data?.status || "finalized");
        } catch (e) {
            setStatus("complete");
            setError(e?.response?.data?.message || e?.response?.data?.error || e?.message || "Failed to finalize scorecard.");
        } finally {
            setFinalizing(false);
        }
    };

    const renderScopeFields = () => {
        if (scopeType === "courseIds") {
            return (
                <div style={styles.formGroup}>
                    <label style={styles.label}>Course IDs</label>
                    <textarea
                        rows={5}
                        value={courseIdsText}
                        onChange={(e) => setCourseIdsText(e.target.value)}
                        placeholder={"COURSE_001\nCOURSE_002\nCOURSE_003"}
                        style={styles.textarea}
                    />
                    <div style={styles.helpText}>Enter course IDs separated by comma, space, or newline.</div>
                </div>
            );
        }

        if (scopeType === "hierarchyNode") {
            return (
                <div style={styles.formGroup}>
                    <label style={styles.label}>Hierarchy Node ID</label>
                    <input
                        type="text"
                        value={hierarchyNodeId}
                        onChange={(e) => setHierarchyNodeId(e.target.value)}
                        placeholder="node_12345"
                        style={styles.input}
                    />
                </div>
            );
        }

        return (
            <div style={styles.formGroup}>
                <label style={styles.label}>Data Source Key</label>
                <input
                    type="text"
                    value={dataSourceKey}
                    onChange={(e) => setDataSourceKey(e.target.value)}
                    placeholder="SIS_TERM_2026_SP"
                    style={styles.input}
                />
            </div>
        );
    };

    const canFinalize = useMemo(() => {
        if (!result || !courses.length) return false;
        return courses.every((course) => {
            const stats = getCourseReviewStats(course);
            return stats.requiredMissingCount === 0;
        });
    }, [result, courses]);

    return (
        <div style={styles.app}>
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>Course Best Practices Audit</h1>
                    <div style={styles.subtitle}>
                        Review detected values, override where needed, and generate the final scorecard.
                    </div>
                </div>

                <div style={styles.headerActions}>
                    {runId && (
                        <button style={styles.secondaryButton} onClick={refreshRun} disabled={loading || polling}>
                            Refresh
                        </button>
                    )}

                    {!!courses.length && (status === "complete" || status === "finalized") && (
                        <button
                            style={styles.secondaryButton}
                            onClick={downloadPdf}
                            disabled={loading || polling || saving || finalizing}
                        >
                            Download PDF
                        </button>
                    )}
                </div>
            </div>

            <div style={styles.topGrid}>
                <div style={styles.panel}>
                    <div style={styles.panelTitle}>Audit Setup</div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Scope Type</label>
                        <div style={styles.radioRow}>
                            <label style={styles.radioLabel}>
                                <input
                                    type="radio"
                                    name="scopeType"
                                    value="courseIds"
                                    checked={scopeType === "courseIds"}
                                    onChange={() => setScopeType("courseIds")}
                                />{" "}
                                Course IDs
                            </label>
                            <label style={styles.radioLabel}>
                                <input
                                    type="radio"
                                    name="scopeType"
                                    value="hierarchyNode"
                                    checked={scopeType === "hierarchyNode"}
                                    onChange={() => setScopeType("hierarchyNode")}
                                />{" "}
                                Hierarchy Node
                            </label>
                            <label style={styles.radioLabel}>
                                <input
                                    type="radio"
                                    name="scopeType"
                                    value="dataSourceKey"
                                    checked={scopeType === "dataSourceKey"}
                                    onChange={() => setScopeType("dataSourceKey")}
                                />{" "}
                                Data Source Key
                            </label>
                        </div>
                    </div>

                    {renderScopeFields()}

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Include Categories</label>
                        <div style={styles.checkboxRow}>
                            <label style={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={includeFunctional}
                                    onChange={(e) => setIncludeFunctional(e.target.checked)}
                                />{" "}
                                Functional
                            </label>
                            <label style={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={includeTechnical}
                                    onChange={(e) => setIncludeTechnical(e.target.checked)}
                                />{" "}
                                Technical
                            </label>
                            <label style={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={includePedagogical}
                                    onChange={(e) => setIncludePedagogical(e.target.checked)}
                                />{" "}
                                Pedagogical
                            </label>
                            <label style={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={includeBehavior}
                                    onChange={(e) => setIncludeBehavior(e.target.checked)}
                                />{" "}
                                Behavior
                            </label>
                        </div>
                    </div>

                    <div style={styles.actionRow}>
                        <button
                            style={styles.primaryButton}
                            onClick={startAudit}
                            disabled={!canRun || loading || polling || saving || finalizing}
                        >
                            {loading || polling ? "Running Audit..." : "Run Audit"}
                        </button>
                    </div>

                    {!token && <div style={styles.errorText}>Missing token query param.</div>}
                    {!userUuid && <div style={styles.errorText}>Missing uuid query param.</div>}
                    {!normalizedPlatformUrl && <div style={styles.errorText}>Missing platformUrl prop.</div>}
                    {!normalizedBackEndUrl && <div style={styles.errorText}>Missing backEndUrl prop.</div>}
                    {error && <div style={styles.errorText}>{error}</div>}
                </div>

                <div style={styles.summaryPanel}>
                    <div style={styles.panelTitle}>Audit Summary</div>

                    <div style={styles.summaryGrid}>
                        <SummaryCard title="Courses in Scope" value={summary.totalCourses || 0} />
                        <SummaryCard title="Courses Audited" value={summary.completedCourses || 0} />
                        <SummaryCard title="Reviewed" value={summary.coursesReviewed || 0} />
                        <SummaryCard title="Need Review" value={summary.metricsNeedingReview || 0} />
                        <SummaryCard title="Manual Overrides" value={summary.manualOverrides || 0} />
                        <SummaryCard title="Avg Overall" value={fmtScore(summary.averageOverallScore)} />
                        <SummaryCard title="Avg Functional" value={fmtScore(summary.averageFunctionalScore)} />
                        <SummaryCard title="Avg Technical" value={fmtScore(summary.averageTechnicalScore)} />
                        <SummaryCard title="Avg Pedagogical" value={fmtScore(summary.averagePedagogicalScore)} />
                        <SummaryCard title="Avg Behavior" value={fmtScore(summary.averageBehaviorScore)} />
                    </div>

                    <div style={styles.statusBar}>
                        <StatusPill status={status} />
                        {polling && <span style={styles.statusHint}>Polling for progress…</span>}
                        {saving && <span style={styles.statusHint}>Saving recalculated scores…</span>}
                        {finalizing && <span style={styles.statusHint}>Generating final scorecard…</span>}
                    </div>

                    {runId && (
                        <div style={styles.helpText}>
                            Run ID: <strong>{runId}</strong>
                        </div>
                    )}
                </div>
            </div>

            {!!courses.length && (
                <div style={styles.mainGrid}>
                    <div style={styles.leftPane}>
                        <div style={styles.panelTitle}>Courses</div>

                        <div style={styles.formGroup}>
                            <input
                                type="text"
                                value={courseFilter}
                                onChange={(e) => setCourseFilter(e.target.value)}
                                placeholder="Filter courses..."
                                style={styles.input}
                            />
                        </div>

                        <div style={{ ...styles.checkboxRow, marginBottom: 12, gap: 10 }}>
                            <label style={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={showOnlyNeedsReview}
                                    onChange={(e) => setShowOnlyNeedsReview(e.target.checked)}
                                />{" "}
                                Needs review
                            </label>
                            <label style={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={showOnlyOverrides}
                                    onChange={(e) => setShowOnlyOverrides(e.target.checked)}
                                />{" "}
                                Overrides
                            </label>
                            <label style={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={showOnlyUnreviewed}
                                    onChange={(e) => setShowOnlyUnreviewed(e.target.checked)}
                                />{" "}
                                Unreviewed
                            </label>
                        </div>

                        <div style={styles.courseList}>
                            {filteredCourses.length === 0 ? (
                                <div style={styles.emptyState}>No courses match the current filters.</div>
                            ) : (
                                filteredCourses.map((course) => {
                                    const stats = getCourseReviewStats(course);
                                    const selected = course.courseId === selectedCourseId;
                                    return (
                                        <button
                                            key={course.courseId}
                                            style={{
                                                ...styles.courseListItem,
                                                ...(selected ? styles.courseListItemSelected : {}),
                                            }}
                                            onClick={() => setSelectedCourseId(course.courseId)}
                                        >
                                            <div style={styles.courseListItemTop}>
                                                <div style={styles.courseName}>{course.courseName || course.courseId}</div>
                                                <ScoreBadge score={course.overallScore} />
                                            </div>

                                            <div style={styles.courseMeta}>{course.courseId}</div>

                                            <div style={styles.courseStatsRow}>
                                                <SmallStat label="Need Review" value={stats.needsReviewCount} />
                                                <SmallStat label="Overrides" value={stats.overrideCount} />
                                                <SmallStat label="Finalized" value={`${stats.finalizedCount}/${stats.totalCount}`} />
                                            </div>

                                            <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                                                {course.reviewed ? (
                                                    <Badge kind="manual">Reviewed</Badge>
                                                ) : (
                                                    <Badge kind="missing">Unreviewed</Badge>
                                                )}
                                                {stats.requiredMissingCount > 0 && (
                                                    <Badge kind="partial">{stats.requiredMissingCount} required missing</Badge>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    <div style={styles.rightPane}>
                        {!selectedCourse ? (
                            <div style={styles.panel}>
                                <div style={styles.emptyState}>Select a course to review.</div>
                            </div>
                        ) : (
                            <>
                                <div style={styles.panel}>
                                    <div style={styles.courseHeader}>
                                        <div>
                                            <div style={styles.courseHeaderTitle}>
                                                {selectedCourse.courseName || selectedCourse.courseId}
                                            </div>
                                            <div style={styles.courseHeaderMeta}>
                                                {selectedCourse.courseId}
                                                {selectedCourse.dataSourceKey ? ` • DSK: ${selectedCourse.dataSourceKey}` : ""}
                                                {selectedCourse.templateCourseId ? ` • Template: ${selectedCourse.templateCourseId}` : ""}
                                            </div>
                                        </div>

                                        <div style={styles.courseHeaderRight}>
                                            <MiniScoreCard title="Overall" score={selectedCourse.overallScore} />
                                            <label style={styles.checkboxLabel}>
                                                <input
                                                    type="checkbox"
                                                    checked={!!selectedCourse.reviewed}
                                                    onChange={(e) => markCourseReviewed(selectedCourse.courseId, e.target.checked)}
                                                />{" "}
                                                Mark Reviewed
                                            </label>
                                        </div>
                                    </div>

                                    <div style={styles.categoryScoreGrid}>
                                        <CategoryScoreCard
                                            title="Functional"
                                            score={selectedCourse.functionalScore}
                                            active={selectedCategory === "functional"}
                                            onClick={() => setSelectedCategory("functional")}
                                        />
                                        <CategoryScoreCard
                                            title="Technical"
                                            score={selectedCourse.technicalScore}
                                            active={selectedCategory === "technical"}
                                            onClick={() => setSelectedCategory("technical")}
                                        />
                                        <CategoryScoreCard
                                            title="Pedagogical"
                                            score={selectedCourse.pedagogicalScore}
                                            active={selectedCategory === "pedagogical"}
                                            onClick={() => setSelectedCategory("pedagogical")}
                                        />
                                        <CategoryScoreCard
                                            title="Behavior"
                                            score={selectedCourse.behaviorScore}
                                            active={selectedCategory === "behavior"}
                                            onClick={() => setSelectedCategory("behavior")}
                                        />
                                    </div>
                                </div>

                                <div style={styles.panel}>
                                    <div style={styles.panelHeaderRow}>
                                        <div style={styles.panelTitle}>
                                            {capitalize(selectedCategory)} Review Items
                                        </div>
                                        <div style={styles.helpText}>
                                            Detected values can be overridden. Missing values can be entered manually.
                                        </div>
                                    </div>

                                    <div style={styles.metricTableWrap}>
                                        <table style={styles.metricTable}>
                                            <thead>
                                            <tr>
                                                <th style={styles.th}>Metric</th>
                                                <th style={styles.th}>Detected Value</th>
                                                <th style={styles.th}>Basis</th>
                                                <th style={styles.th}>Confidence</th>
                                                <th style={styles.th}>Reviewer Value</th>
                                                <th style={styles.th}>Final Value</th>
                                                <th style={styles.th}>Weight</th>
                                                <th style={styles.th}>Score</th>
                                                <th style={styles.th}>Notes</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {selectedMetrics.length === 0 ? (
                                                <tr>
                                                    <td colSpan={9} style={styles.emptyTableCell}>
                                                        No metrics found for this category.
                                                    </td>
                                                </tr>
                                            ) : (
                                                selectedMetrics.map((metric) => (
                                                    <MetricReviewRow
                                                        key={metric.key}
                                                        metric={metric}
                                                        onChange={(patch) => updateMetric(selectedCourse.courseId, metric.key, patch)}
                                                    />
                                                ))
                                            )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div style={styles.panel}>
                                    <div style={styles.panelHeaderRow}>
                                        <div style={styles.panelTitle}>Course Highlights</div>
                                        <div style={styles.helpText}>Optional narrative output for the final scorecard.</div>
                                    </div>

                                    <EditableStringList
                                        label="Highlights"
                                        items={selectedCourse.highlights || []}
                                        onChange={(items) => {
                                            setResult((prev) => {
                                                if (!prev) return prev;
                                                const nextCourses = (prev.courses || []).map((course) =>
                                                    course.courseId === selectedCourse.courseId ? { ...course, highlights: items } : course
                                                );
                                                return recalcAuditSummary({ ...prev, courses: nextCourses });
                                            });
                                        }}
                                    />

                                    <EditableStringList
                                        label="Recommendations"
                                        items={selectedCourse.recommendations || []}
                                        onChange={(items) => {
                                            setResult((prev) => {
                                                if (!prev) return prev;
                                                const nextCourses = (prev.courses || []).map((course) =>
                                                    course.courseId === selectedCourse.courseId ? { ...course, recommendations: items } : course
                                                );
                                                return recalcAuditSummary({ ...prev, courses: nextCourses });
                                            });
                                        }}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {!!courses.length && (
                <div style={styles.footerBar}>
                    <div style={styles.footerStatus}>
                        {canFinalize ? (
                            <span style={{ color: "#065f46", fontWeight: 600 }}>
                                All required metrics have final values.
                            </span>
                        ) : (
                            <span style={{ color: "#92400e", fontWeight: 600 }}>
                                Some required metrics still need final values.
                            </span>
                        )}
                    </div>

                    <div style={styles.footerActions}>
                        <button
                            style={styles.secondaryButton}
                            onClick={saveRecalculated}
                            disabled={saving || finalizing || polling}
                        >
                            {saving ? "Recalculating..." : "Recalculate Scores"}
                        </button>

                        <button
                            style={{
                                ...styles.primaryButton,
                                opacity: canFinalize ? 1 : 0.7,
                            }}
                            onClick={finalizeScorecard}
                            disabled={finalizing || saving || polling || !canFinalize}
                        >
                            {finalizing ? "Generating..." : "Generate Final Scorecard"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

/* -------------------------------------------------------------------------- */
/* Metric Row                                                                  */
/* -------------------------------------------------------------------------- */

const MetricReviewRow = ({ metric, onChange }) => {
    const finalValue = getMetricFinalValue(metric);
    const detectedDisplay = formatMetricValue(metric.valueType, metric.detectedValue, metric.options);
    const finalDisplay = formatMetricValue(metric.valueType, finalValue, metric.options);

    const renderEditor = () => {
        if (metric.valueType === "boolean") {
            return (
                <select
                    value={metric.reviewerValue == null ? "" : String(metric.reviewerValue)}
                    onChange={(e) =>
                        onChange({
                            reviewerValue:
                                e.target.value === ""
                                    ? null
                                    : e.target.value === "true",
                        })
                    }
                    style={styles.inlineSelect}
                >
                    <option value="">Select…</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                </select>
            );
        }

        if (metric.valueType === "number" || metric.valueType === "percent") {
            return (
                <input
                    type="number"
                    value={metric.reviewerValue ?? ""}
                    onChange={(e) =>
                        onChange({
                            reviewerValue: e.target.value === "" ? null : Number(e.target.value),
                        })
                    }
                    style={styles.inlineInput}
                />
            );
        }

        if (metric.valueType === "enum") {
            return (
                <select
                    value={metric.reviewerValue ?? ""}
                    onChange={(e) =>
                        onChange({
                            reviewerValue: e.target.value === "" ? null : e.target.value,
                        })
                    }
                    style={styles.inlineSelect}
                >
                    <option value="">Select…</option>
                    {(metric.options || []).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
            );
        }

        return (
            <textarea
                rows={2}
                value={metric.reviewerValue ?? ""}
                onChange={(e) =>
                    onChange({
                        reviewerValue: e.target.value,
                    })
                }
                style={styles.inlineTextarea}
            />
        );
    };

    return (
        <tr>
            <td style={styles.td}>
                <div style={{ fontWeight: 700 }}>{metric.label}</div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>{metric.description}</div>
                <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <Badge kind={metric.status}>{statusLabel(metric.status)}</Badge>
                    {metric.required && <Badge kind="partial">Required</Badge>}
                </div>
            </td>

            <td style={styles.td}>
                <div>{detectedDisplay || "—"}</div>
            </td>

            <td style={styles.td}>
                <div style={{ fontSize: 13 }}>{metric.basis || "—"}</div>
            </td>

            <td style={styles.td}>
                <ConfidencePill value={metric.confidence} />
            </td>

            <td style={styles.td}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                    <input
                        type="checkbox"
                        checked={!!metric.useReviewerValue}
                        onChange={(e) =>
                            onChange({
                                useReviewerValue: e.target.checked,
                            })
                        }
                    />
                    <span style={{ fontSize: 13 }}>Use reviewer value</span>
                </label>

                {renderEditor()}
            </td>

            <td style={styles.td}>
                <div style={{ fontWeight: 600 }}>{finalDisplay || "—"}</div>
            </td>

            <td style={styles.td}>{metric.weight ?? 0}</td>

            <td style={styles.td}>
                <div style={{ fontWeight: 700 }}>{fmtScore(metric.score)}</div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                    / {fmtScore(metric.maxScore)}
                </div>
            </td>

            <td style={styles.td}>
                <textarea
                    rows={2}
                    value={metric.notes || ""}
                    onChange={(e) =>
                        onChange({
                            notes: e.target.value,
                        })
                    }
                    style={styles.inlineTextarea}
                    placeholder="Reviewer notes..."
                />
            </td>
        </tr>
    );
};

/* -------------------------------------------------------------------------- */
/* Small Components                                                            */
/* -------------------------------------------------------------------------- */

const SummaryCard = ({ title, value }) => (
    <div style={styles.summaryCard}>
        <div style={styles.summaryCardLabel}>{title}</div>
        <div style={styles.summaryCardValue}>{value ?? "—"}</div>
    </div>
);

const SmallStat = ({ label, value }) => (
    <div style={styles.smallStat}>
        <div style={styles.smallStatValue}>{value}</div>
        <div style={styles.smallStatLabel}>{label}</div>
    </div>
);

const ScoreBadge = ({ score }) => {
    const tone = scoreTone(score);
    return (
        <div
            style={{
                ...styles.scoreBadge,
                background: tone.background,
                color: tone.color,
                borderColor: tone.border,
            }}
        >
            {fmtScore(score)}
        </div>
    );
};

const StatusPill = ({ status }) => {
    const map = {
        idle: { bg: "#f3f4f6", color: "#374151", label: "Idle" },
        starting: { bg: "#eff6ff", color: "#1d4ed8", label: "Starting" },
        running: { bg: "#eff6ff", color: "#1d4ed8", label: "Running" },
        complete: { bg: "#ecfdf5", color: "#065f46", label: "Complete" },
        finalized: { bg: "#dcfce7", color: "#166534", label: "Finalized" },
        finalizing: { bg: "#fffbeb", color: "#92400e", label: "Finalizing" },
        failed: { bg: "#fef2f2", color: "#991b1b", label: "Failed" },
    };
    const tone = map[status] || map.idle;
    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                borderRadius: 999,
                padding: "6px 10px",
                fontWeight: 700,
                fontSize: 12,
                background: tone.bg,
                color: tone.color,
            }}
        >
            {tone.label}
        </span>
    );
};

const Badge = ({ kind, children }) => {
    const tone = badgeTone(kind);
    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                borderRadius: 999,
                padding: "4px 8px",
                fontWeight: 600,
                fontSize: 12,
                background: tone.background,
                color: tone.color,
                border: `1px solid ${tone.border}`,
            }}
        >
            {children}
        </span>
    );
};

const ConfidencePill = ({ value }) => {
    const normalized = String(value || "").toLowerCase();
    const tone =
        normalized === "high"
            ? { background: "#ecfdf5", color: "#065f46", border: "#a7f3d0" }
            : normalized === "medium"
                ? { background: "#fffbeb", color: "#92400e", border: "#fde68a" }
                : { background: "#f3f4f6", color: "#374151", border: "#d1d5db" };

    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                borderRadius: 999,
                padding: "4px 8px",
                fontWeight: 600,
                fontSize: 12,
                background: tone.background,
                color: tone.color,
                border: `1px solid ${tone.border}`,
            }}
        >
            {capitalize(normalized || "unknown")}
        </span>
    );
};

const MiniScoreCard = ({ title, score }) => (
    <div style={styles.miniScoreCard}>
        <div style={styles.miniScoreLabel}>{title}</div>
        <div style={styles.miniScoreValue}>{fmtScore(score)}</div>
    </div>
);

const CategoryScoreCard = ({ title, score, active, onClick }) => {
    const tone = scoreTone(score);
    return (
        <button
            onClick={onClick}
            style={{
                ...styles.categoryCard,
                ...(active ? styles.categoryCardActive : {}),
                borderColor: active ? "#2563eb" : "#e5e7eb",
            }}
        >
            <div style={styles.categoryCardTitle}>{title}</div>
            <div
                style={{
                    ...styles.categoryCardScore,
                    background: tone.background,
                    color: tone.color,
                    border: `1px solid ${tone.border}`,
                }}
            >
                {fmtScore(score)}
            </div>
        </button>
    );
};

const EditableStringList = ({ label, items, onChange }) => {
    const [draft, setDraft] = useState("");

    const addItem = () => {
        const value = String(draft || "").trim();
        if (!value) return;
        onChange([...(items || []), value]);
        setDraft("");
    };

    const removeItem = (index) => {
        onChange((items || []).filter((_, i) => i !== index));
    };

    const updateItem = (index, value) => {
        onChange((items || []).map((item, i) => (i === index ? value : item)));
    };

    return (
        <div style={{ marginBottom: 18 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>{label}</div>

            {(items || []).length === 0 ? (
                <div style={styles.emptyStateSmall}>No {label.toLowerCase()} yet.</div>
            ) : (
                <div style={{ display: "grid", gap: 8, marginBottom: 10 }}>
                    {(items || []).map((item, idx) => (
                        <div key={`${label}-${idx}`} style={styles.listEditRow}>
                            <input
                                type="text"
                                value={item}
                                onChange={(e) => updateItem(idx, e.target.value)}
                                style={styles.input}
                            />
                            <button style={styles.dangerButton} onClick={() => removeItem(idx)}>
                                Remove
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <div style={styles.listEditRow}>
                <input
                    type="text"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={`Add ${label.toLowerCase()}...`}
                    style={styles.input}
                />
                <button style={styles.secondaryButton} onClick={addItem}>
                    Add
                </button>
            </div>
        </div>
    );
};

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

function normalizeBase(v) {
    return String(v || "").trim().replace(/\/+$/, "");
}

function parseCourseIds(text) {
    return String(text || "")
        .split(/[\n,\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);
}

function capitalize(v) {
    const s = String(v || "");
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
}

function fmtScore(v) {
    if (v == null || v === "") return "—";
    const n = Number(v);
    if (!Number.isFinite(n)) return "—";
    return n.toFixed(1);
}

function formatMetricValue(valueType, value, options = []) {
    if (value == null || value === "") return "";
    if (valueType === "boolean") return value ? "Yes" : "No";
    if (valueType === "percent") return `${Number(value)}%`;
    if (valueType === "enum") {
        const match = (options || []).find((opt) => String(opt.value) === String(value));
        return match?.label || String(value);
    }
    return String(value);
}

function getMetricFinalValue(metric) {
    if (metric.useReviewerValue) return metric.reviewerValue;
    if (metric.detectedValue != null && metric.detectedValue !== "") return metric.detectedValue;
    return metric.reviewerValue;
}

function statusLabel(status) {
    if (status === "computed") return "Computed";
    if (status === "partial") return "Partial";
    if (status === "missing") return "Needs Review";
    if (status === "manual") return "Reviewer Value";
    if (status === "locked") return "Locked";
    return capitalize(status || "unknown");
}

function badgeTone(kind) {
    switch (kind) {
        case "computed":
            return { background: "#ecfdf5", color: "#065f46", border: "#a7f3d0" };
        case "partial":
            return { background: "#fffbeb", color: "#92400e", border: "#fde68a" };
        case "missing":
            return { background: "#fef2f2", color: "#991b1b", border: "#fecaca" };
        case "manual":
            return { background: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" };
        case "locked":
            return { background: "#f3f4f6", color: "#374151", border: "#d1d5db" };
        default:
            return { background: "#f9fafb", color: "#374151", border: "#e5e7eb" };
    }
}

function scoreTone(score) {
    const s = Number(score || 0);
    if (s >= 85) return { background: "#ecfdf5", color: "#065f46", border: "#a7f3d0" };
    if (s >= 70) return { background: "#fffbeb", color: "#92400e", border: "#fde68a" };
    return { background: "#fef2f2", color: "#991b1b", border: "#fecaca" };
}

function deriveMetric(metric) {
    const finalValue = getMetricFinalValue(metric);
    const hasDetected = metric.detectedValue != null && metric.detectedValue !== "";
    const hasReviewer = metric.reviewerValue != null && metric.reviewerValue !== "";

    let status = metric.status;
    if (metric.useReviewerValue && hasReviewer) {
        status = "manual";
    } else if (hasDetected && metric.confidence === "high") {
        status = "computed";
    } else if (hasDetected) {
        status = "partial";
    } else if (hasReviewer) {
        status = "manual";
    } else {
        status = "missing";
    }

    const score = computeMetricScore({ ...metric, finalValue });
    return {
        ...metric,
        finalValue,
        status,
        score,
    };
}

function computeMetricScore(metric) {
    const maxScore = Number(metric.maxScore ?? metric.weight ?? 0);
    const finalValue = metric.finalValue;

    if (finalValue == null || finalValue === "") return 0;

    if (metric.scoringType === "boolean") {
        return finalValue ? maxScore : 0;
    }

    if (metric.scoringType === "threshold") {
        const threshold = Number(metric.threshold ?? 0);
        const n = Number(finalValue);
        if (!Number.isFinite(n)) return 0;
        return n >= threshold ? maxScore : Math.max(0, (n / Math.max(threshold, 1)) * maxScore);
    }

    if (metric.scoringType === "percent") {
        const n = Number(finalValue);
        if (!Number.isFinite(n)) return 0;
        return Math.max(0, Math.min(maxScore, (n / 100) * maxScore));
    }

    if (metric.scoringType === "count") {
        const n = Number(finalValue);
        const target = Number(metric.targetCount ?? 1);
        if (!Number.isFinite(n)) return 0;
        return Math.max(0, Math.min(maxScore, (n / Math.max(target, 1)) * maxScore));
    }

    if (metric.scoringType === "enum") {
        const valueMap = metric.enumScores || {};
        const raw = valueMap[String(finalValue)];
        if (raw == null) return 0;
        return Math.max(0, Math.min(maxScore, Number(raw)));
    }

    if (metric.scoringType === "manual") {
        const n = Number(finalValue);
        if (!Number.isFinite(n)) return 0;
        return Math.max(0, Math.min(maxScore, n));
    }

    if (metric.valueType === "boolean") {
        return finalValue ? maxScore : 0;
    }

    return maxScore;
}

function recalcCourseScores(course) {
    const metrics = (course.metrics || []).map((m) => deriveMetric(m));

    const grouped = {
        functional: metrics.filter((m) => m.category === "functional"),
        technical: metrics.filter((m) => m.category === "technical"),
        pedagogical: metrics.filter((m) => m.category === "pedagogical"),
        behavior: metrics.filter((m) => m.category === "behavior"),
    };

    const functionalScore = computeCategoryScore(grouped.functional);
    const technicalScore = computeCategoryScore(grouped.technical);
    const pedagogicalScore = computeCategoryScore(grouped.pedagogical);
    const behaviorScore = computeCategoryScore(grouped.behavior);

    const includedScores = [functionalScore, technicalScore, pedagogicalScore, behaviorScore].filter((n) => n != null);
    const overallScore =
        includedScores.length > 0
            ? includedScores.reduce((sum, n) => sum + n, 0) / includedScores.length
            : 0;

    return {
        ...course,
        metrics,
        functionalScore,
        technicalScore,
        pedagogicalScore,
        behaviorScore,
        overallScore,
    };
}

function computeCategoryScore(metrics) {
    const list = metrics || [];
    const totalPossible = list.reduce((sum, m) => sum + Number(m.maxScore ?? m.weight ?? 0), 0);
    const totalEarned = list.reduce((sum, m) => sum + Number(m.score ?? 0), 0);
    if (!totalPossible) return 0;
    return (totalEarned / totalPossible) * 100;
}

function recalcAuditSummary(result) {
    const courses = (result?.courses || []).map((course) => recalcCourseScores(course));

    const totalCourses = courses.length;
    const completedCourses = courses.length;
    const reviewedCourses = courses.filter((c) => c.reviewed).length;

    const averageOverallScore = avg(courses.map((c) => c.overallScore));
    const averageFunctionalScore = avg(courses.map((c) => c.functionalScore));
    const averageTechnicalScore = avg(courses.map((c) => c.technicalScore));
    const averagePedagogicalScore = avg(courses.map((c) => c.pedagogicalScore));
    const averageBehaviorScore = avg(courses.map((c) => c.behaviorScore));

    const metricsNeedingReview = courses.reduce((sum, c) => sum + getCourseReviewStats(c).needsReviewCount, 0);
    const manualOverrides = courses.reduce((sum, c) => sum + getCourseReviewStats(c).overrideCount, 0);

    return {
        ...result,
        courses,
        summary: {
            ...(result?.summary || {}),
            totalCourses,
            completedCourses,
            coursesReviewed: reviewedCourses,
            averageOverallScore,
            averageFunctionalScore,
            averageTechnicalScore,
            averagePedagogicalScore,
            averageBehaviorScore,
            metricsNeedingReview,
            manualOverrides,
        },
    };
}

function avg(values) {
    const list = (values || []).filter((v) => Number.isFinite(Number(v)));
    if (!list.length) return 0;
    return list.reduce((sum, v) => sum + Number(v), 0) / list.length;
}

function getCourseReviewStats(course) {
    const metrics = course.metrics || [];
    const totalCount = metrics.length;
    const needsReviewCount = metrics.filter((m) => m.status === "missing" || m.status === "partial").length;
    const overrideCount = metrics.filter((m) => !!m.useReviewerValue).length;
    const finalizedCount = metrics.filter((m) => getMetricFinalValue(m) != null && getMetricFinalValue(m) !== "").length;
    const requiredMissingCount = metrics.filter(
        (m) => m.required && (getMetricFinalValue(m) == null || getMetricFinalValue(m) === "")
    ).length;

    return {
        totalCount,
        needsReviewCount,
        overrideCount,
        finalizedCount,
        requiredMissingCount,
    };
}

function buildReviewedPayload(result, platformUrl, userUuid) {
    return {
        runId: result?.runId,
        platformUrl,
        userUuid,
        courses: (result?.courses || []).map((course) => ({
            courseId: course.courseId,
            reviewed: !!course.reviewed,
            highlights: course.highlights || [],
            recommendations: course.recommendations || [],
            metrics: (course.metrics || []).map((metric) => ({
                key: metric.key,
                detectedValue: metric.detectedValue ?? null,
                reviewerValue: metric.reviewerValue ?? null,
                useReviewerValue: !!metric.useReviewerValue,
                finalValue: getMetricFinalValue(metric),
                notes: metric.notes || "",
                status: metric.status,
                score: metric.score ?? 0,
            })),
        })),
    };
}

function hydrateAuditResult(data) {
    const base = {
        ...data,
        runId: data?.runId || "",
        status: data?.status || "complete",
        summary: data?.summary || {},
        courses: (data?.courses || []).map((course) => {
            const metrics = (course.metrics || []).map((metric) => {
                const normalized = {
                    key: metric.key,
                    label: metric.label || metric.key,
                    description: metric.description || "",
                    category: metric.category || "functional",
                    valueType: metric.valueType || "boolean",
                    scoringType: metric.scoringType || inferScoringType(metric),
                    required: !!metric.required,
                    detectedValue:
                        metric.detectedValue !== undefined
                            ? metric.detectedValue
                            : metric.computedValue !== undefined
                                ? metric.computedValue
                                : null,
                    reviewerValue:
                        metric.reviewerValue !== undefined
                            ? metric.reviewerValue
                            : metric.manualValue !== undefined
                                ? metric.manualValue
                                : null,
                    useReviewerValue:
                        metric.useReviewerValue !== undefined
                            ? metric.useReviewerValue
                            : !!metric.useManualOverride,
                    finalValue:
                        metric.finalValue !== undefined
                            ? metric.finalValue
                            : null,
                    status: metric.status || inferInitialStatus(metric),
                    confidence: metric.confidence || "low",
                    basis: metric.basis || metric.source || "",
                    weight: Number(metric.weight ?? metric.maxScore ?? 0),
                    maxScore: Number(metric.maxScore ?? metric.weight ?? 0),
                    score: Number(metric.score ?? 0),
                    threshold: metric.threshold,
                    targetCount: metric.targetCount,
                    enumScores: metric.enumScores || {},
                    options: metric.options || [],
                    notes: metric.notes || "",
                };

                return deriveMetric(normalized);
            });

            return recalcCourseScores({
                ...course,
                reviewed: !!course.reviewed,
                metrics,
                highlights: course.highlights || [],
                recommendations: course.recommendations || [],
            });
        }),
    };

    return recalcAuditSummary(base);
}

function inferScoringType(metric) {
    if (metric.scoringType) return metric.scoringType;
    if (metric.valueType === "boolean") return "boolean";
    if (metric.valueType === "percent") return "percent";
    if (metric.valueType === "number") return "threshold";
    if (metric.valueType === "enum") return "enum";
    return "manual";
}

function inferInitialStatus(metric) {
    const detectedValue =
        metric.detectedValue !== undefined
            ? metric.detectedValue
            : metric.computedValue !== undefined
                ? metric.computedValue
                : null;

    if (detectedValue == null || detectedValue === "") return "missing";
    if (String(metric.confidence || "").toLowerCase() === "high") return "computed";
    return "partial";
}

function sanitizeFileName(value) {
    return String(value || "course-best-practices-audit")
        .replace(/[\\/:*?"<>|]+/g, "_")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 120);
}

function pdfText(doc, text, x, y, maxWidth, lineHeight = 13) {
    const lines = doc.splitTextToSize(String(text || ""), maxWidth);
    let yy = y;
    lines.forEach((line) => {
        doc.text(line, x, yy);
        yy += lineHeight;
    });
    return yy;
}

function metricDisplayValue(metric) {
    return formatMetricValue(
        metric?.valueType,
        getMetricFinalValue(metric),
        metric?.options || []
    ) || "—";
}

/* -------------------------------------------------------------------------- */
/* Styles                                                                      */
/* -------------------------------------------------------------------------- */

const styles = {
    app: {
        fontFamily: "Arial, Helvetica, sans-serif",
        color: "#111827",
        background: "#f9fafb",
        minHeight: "100vh",
        padding: 16,
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 16,
        marginBottom: 16,
    },
    title: {
        margin: 0,
        fontSize: 28,
        fontWeight: 800,
    },
    subtitle: {
        marginTop: 6,
        color: "#6b7280",
        fontSize: 14,
    },
    headerActions: {
        display: "flex",
        gap: 8,
        alignItems: "center",
    },
    topGrid: {
        display: "grid",
        gridTemplateColumns: "1.1fr 1fr",
        gap: 16,
        marginBottom: 16,
    },
    mainGrid: {
        display: "grid",
        gridTemplateColumns: "340px 1fr",
        gap: 16,
    },
    leftPane: {
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 14,
        minHeight: 400,
    },
    rightPane: {
        display: "grid",
        gap: 16,
    },
    panel: {
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 14,
    },
    summaryPanel: {
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 14,
    },
    panelTitle: {
        fontSize: 18,
        fontWeight: 800,
        marginBottom: 12,
    },
    panelHeaderRow: {
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        alignItems: "center",
        flexWrap: "wrap",
        marginBottom: 10,
    },
    formGroup: {
        marginBottom: 14,
    },
    label: {
        display: "block",
        marginBottom: 6,
        fontWeight: 700,
        fontSize: 14,
    },
    input: {
        width: "100%",
        boxSizing: "border-box",
        padding: "10px 12px",
        borderRadius: 8,
        border: "1px solid #d1d5db",
        fontSize: 14,
        background: "#fff",
    },
    textarea: {
        width: "100%",
        boxSizing: "border-box",
        padding: "10px 12px",
        borderRadius: 8,
        border: "1px solid #d1d5db",
        fontSize: 14,
        background: "#fff",
        resize: "vertical",
    },
    helpText: {
        fontSize: 12,
        color: "#6b7280",
        marginTop: 6,
    },
    radioRow: {
        display: "flex",
        gap: 16,
        flexWrap: "wrap",
    },
    checkboxRow: {
        display: "flex",
        gap: 16,
        flexWrap: "wrap",
    },
    radioLabel: {
        fontSize: 14,
    },
    checkboxLabel: {
        fontSize: 14,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
    },
    actionRow: {
        display: "flex",
        gap: 8,
        marginTop: 12,
    },
    primaryButton: {
        background: "#2563eb",
        color: "#fff",
        border: "none",
        borderRadius: 8,
        padding: "10px 14px",
        fontWeight: 700,
        cursor: "pointer",
    },
    secondaryButton: {
        background: "#fff",
        color: "#111827",
        border: "1px solid #d1d5db",
        borderRadius: 8,
        padding: "10px 14px",
        fontWeight: 700,
        cursor: "pointer",
        textDecoration: "none",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
    },
    dangerButton: {
        background: "#fff",
        color: "#991b1b",
        border: "1px solid #fecaca",
        borderRadius: 8,
        padding: "10px 14px",
        fontWeight: 700,
        cursor: "pointer",
    },
    errorText: {
        marginTop: 10,
        color: "#991b1b",
        fontSize: 13,
        fontWeight: 600,
    },
    summaryGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
        gap: 10,
        marginBottom: 14,
    },
    summaryCard: {
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: 12,
        background: "#fff",
    },
    summaryCardLabel: {
        fontSize: 12,
        color: "#6b7280",
        marginBottom: 6,
    },
    summaryCardValue: {
        fontSize: 24,
        fontWeight: 800,
    },
    statusBar: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
        marginBottom: 4,
    },
    statusHint: {
        fontSize: 13,
        color: "#6b7280",
    },
    courseList: {
        display: "grid",
        gap: 10,
        maxHeight: "70vh",
        overflowY: "auto",
    },
    courseListItem: {
        textAlign: "left",
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: 12,
        cursor: "pointer",
    },
    courseListItemSelected: {
        border: "2px solid #2563eb",
        background: "#eff6ff",
    },
    courseListItemTop: {
        display: "flex",
        justifyContent: "space-between",
        gap: 10,
        alignItems: "center",
    },
    courseName: {
        fontWeight: 800,
        fontSize: 15,
    },
    courseMeta: {
        fontSize: 12,
        color: "#6b7280",
        marginTop: 4,
    },
    courseStatsRow: {
        display: "flex",
        gap: 8,
        marginTop: 10,
        flexWrap: "wrap",
    },
    smallStat: {
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: "6px 8px",
        background: "#f9fafb",
        minWidth: 70,
    },
    smallStatValue: {
        fontWeight: 800,
        fontSize: 13,
    },
    smallStatLabel: {
        fontSize: 11,
        color: "#6b7280",
    },
    scoreBadge: {
        display: "inline-flex",
        minWidth: 58,
        justifyContent: "center",
        alignItems: "center",
        borderRadius: 999,
        border: "1px solid",
        padding: "6px 10px",
        fontWeight: 800,
        fontSize: 13,
    },
    courseHeader: {
        display: "flex",
        justifyContent: "space-between",
        gap: 14,
        alignItems: "flex-start",
        flexWrap: "wrap",
    },
    courseHeaderTitle: {
        fontSize: 22,
        fontWeight: 800,
    },
    courseHeaderMeta: {
        marginTop: 6,
        color: "#6b7280",
        fontSize: 13,
    },
    courseHeaderRight: {
        display: "flex",
        gap: 10,
        alignItems: "center",
        flexWrap: "wrap",
    },
    miniScoreCard: {
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: "8px 12px",
        background: "#fff",
        minWidth: 90,
    },
    miniScoreLabel: {
        fontSize: 12,
        color: "#6b7280",
    },
    miniScoreValue: {
        marginTop: 4,
        fontSize: 22,
        fontWeight: 800,
    },
    categoryScoreGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(4, minmax(140px, 1fr))",
        gap: 10,
        marginTop: 14,
    },
    categoryCard: {
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: 12,
        background: "#fff",
        textAlign: "left",
        cursor: "pointer",
    },
    categoryCardActive: {
        boxShadow: "0 0 0 2px rgba(37,99,235,0.15)",
        background: "#f8fbff",
    },
    categoryCardTitle: {
        fontWeight: 800,
        fontSize: 14,
        marginBottom: 8,
    },
    categoryCardScore: {
        display: "inline-flex",
        borderRadius: 999,
        padding: "5px 10px",
        fontWeight: 800,
        fontSize: 13,
    },
    metricTableWrap: {
        overflowX: "auto",
    },
    metricTable: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: 14,
    },
    th: {
        textAlign: "left",
        padding: "10px 8px",
        borderBottom: "1px solid #e5e7eb",
        fontSize: 13,
        fontWeight: 800,
        color: "#374151",
        verticalAlign: "top",
        background: "#f9fafb",
        position: "sticky",
        top: 0,
    },
    td: {
        padding: "12px 8px",
        borderBottom: "1px solid #f3f4f6",
        verticalAlign: "top",
    },
    emptyTableCell: {
        padding: 20,
        textAlign: "center",
        color: "#6b7280",
    },
    inlineInput: {
        width: "100%",
        boxSizing: "border-box",
        padding: "8px 10px",
        borderRadius: 8,
        border: "1px solid #d1d5db",
        fontSize: 13,
    },
    inlineSelect: {
        width: "100%",
        boxSizing: "border-box",
        padding: "8px 10px",
        borderRadius: 8,
        border: "1px solid #d1d5db",
        fontSize: 13,
        background: "#fff",
    },
    inlineTextarea: {
        width: "100%",
        boxSizing: "border-box",
        padding: "8px 10px",
        borderRadius: 8,
        border: "1px solid #d1d5db",
        fontSize: 13,
        resize: "vertical",
    },
    footerBar: {
        marginTop: 16,
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 14,
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        alignItems: "center",
        flexWrap: "wrap",
    },
    footerStatus: {
        fontSize: 14,
    },
    footerActions: {
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
    },
    emptyState: {
        color: "#6b7280",
        fontSize: 14,
        padding: 10,
    },
    emptyStateSmall: {
        color: "#6b7280",
        fontSize: 13,
        marginBottom: 10,
    },
    listEditRow: {
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 8,
        alignItems: "center",
    },
};

export default CourseBestPracticesAuditApp;